import { useState, useRef, useEffect } from "react";
import { ChevronDownIcon } from "lucide-react";

interface SideMultiSelectProps {
  label: string; // "프리셉티" 또는 "프리셉터"
  labelColor?: string; // 레이블 색상
  value: string[]; // 선택된 값들 (nurse_id 배열)
  onChange: (values: string[]) => void;
  options: Array<{ label: string; value: string }>; // 간호사 목록
  placeholder?: string;
  className?: string;
  buttonClassName?: string;
  dropdownClassName?: string;
  disabled?: boolean;
}

export default function SideMultiSelect({
  label,
  labelColor = "#0E59C6",
  value,
  onChange,
  options,
  placeholder = "선택",
  className = "",
  buttonClassName = "",
  dropdownClassName = "",
  disabled = false,
}: SideMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // 선택/해제 토글
  const handleToggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      // 이미 선택되어 있으면 제거
      onChange(value.filter((v) => v !== optionValue));
    } else {
      // 선택되어 있지 않으면 추가
      onChange([...value, optionValue]);
    }
  };

  // 선택된 항목들의 이름 가져오기
  const selectedNames = value
    .map((v) => options.find((opt) => opt.value === v)?.label)
    .filter(Boolean)
    .join(", ");

  const displayValue = selectedNames || placeholder;

  return (
    <div ref={selectRef} className={`relative min-w-0 ${className}`}>
      {/* 셀렉트 버튼 */}
      <button
        type="button"
        className={`text-left font-pretendard border 
                    rounded-[5px] w-full border-[#D6D6DE] 
                    text-[15px] relative flex min-w-0 items-center justify-between overflow-hidden px-[15px]
                    h-[35px]
                    ${buttonClassName} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <div className="flex min-w-0 flex-1 items-center justify-start overflow-hidden">
          {/* 고정 레이블 */}
          <span
            className="font-pretendard font-semibold mr-1 flex-shrink-0 text-[15px] pointer-events-none"
            style={{ color: labelColor }}
          >
            {label} |
          </span>

          {/* 선택된 이름들 */}
          <span className="text-[15px] font-pretendard text-[#333333] min-w-0 flex-1 truncate">
            {displayValue}
          </span>
        </div>

        {/* 화살표 아이콘 - shadcn/ui Select와 동일한 스타일 */}
        <ChevronDownIcon className="size-4 opacity-50 pointer-events-none flex-shrink-0" />
      </button>

      {/* 드롭다운 */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Menu */}
          <div
            className={`absolute top-full mt-1 left-0 right-0 bg-white border border-[#D6D6DE] rounded-[5px] shadow-lg z-20 font-pretendard max-h-[200px] overflow-y-auto ${dropdownClassName}`}
          >
            {options.map((option) => {
              const isSelected = value.includes(option.value);
              return (
                <button
                  key={option.value}
                  onClick={() => handleToggle(option.value)}
                  className={`w-full px-4 py-2 text-[15px] text-left hover:bg-gray-100 transition-colors font-pretendard flex items-center justify-between ${
                    isSelected
                      ? "bg-blue-50 text-[#0E59C6] font-medium"
                      : "text-gray-700"
                  }`}
                >
                  <span>{option.label}</span>
                  {/* 체크박스 또는 체크 아이콘 */}
                  {isSelected && (
                    <span className="text-[#0E59C6] text-lg">✓</span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
